import React, { createContext, useContext, useMemo, ReactNode } from "react";
import { createTheme } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";

type ColorModeContextType = {
    toggleColorMode: () => void;
};

const ColorModeContext = createContext<ColorModeContextType | undefined>(undefined);
export const useColorMode = (): ColorModeContextType => {
    const context = useContext(ColorModeContext);
    if (!context) {
        throw new Error("useColorMode must be used within a ColorModeProvider");
    }
    return context;
};
type ThemeModeProviderProps = {
    children: ReactNode;
    mode: "light" | "dark";
    toggleColorMode: () => void
};

const ThemeModeProvider = ({ children, mode, toggleColorMode }: ThemeModeProviderProps) => {
    const lightTheme = useMemo(
        () =>
            createTheme({
                palette: {
                    mode,
                    primary: {
                        main: "#fff",
                        light: "#f7f7f7"
                    },
                    secondary: {
                        main: "#121212"
                    },
                    text: {
                        primary: "#121212",
                        secondary: "gray",
                    },
                    divider: "#a0a0a0",
                    //@ts-ignore
                    customColors: {
                        purple: "rgb(201, 126, 245)"
                    },
                },
                //@ts-ignore
                shadows: [
                    "none", // 0
                    "0 2px 4px 0 rgba(0,0,0,0.2)", // 1
                    "0 3px 6px 0 rgba(0,0,0,0.2)", // 2
                    "0 4px 8px 0 rgba(0,0,0,0.2)", // 3
                    "0 6px 12px 0 rgba(0,0,0,0.2)", // 4
                    "0 8px 16px 0 rgba(0,0,0,0.2)", // 5
                    "0 12px 24px 0 rgba(0,0,0,0.2)", // 6
                    "0 16px 32px 0 rgba(0,0,0,0.2)", // 7
                    "0 24px 48px 0 rgba(0,0,0,0.2)", // 8
                ],
                breakpoints: {
                    values: {
                        xs: 0,
                        sm: 600,
                        md: 960,
                        lg: 1280,
                        xl: 1920
                    },
                },
            }),
        [mode]
    );
    const darkTheme = useMemo(
        () =>
            createTheme({
                palette: {
                    mode,
                    primary: {
                        main: "#121212",
                        light: "rgb(24, 24, 24)",
                        dark: "#353535"
                    },
                    secondary: {
                        main: "#fff",
                    },
                    background: {
                        default: "#121212",
                        paper: "#121212",
                    },
                    text: {
                        primary: "#fff",
                        secondary: "gray",
                    },
                    divider: "#a0a0a0",
                    //@ts-ignore
                    customColors: {
                        purple: "rgb(201, 126, 245)"
                    },

                },
                //@ts-ignore
                shadows: [
                    "none", // 0
                    "0 2px 4px 0 rgba(0,0,0,0.2)", // 1
                    "0 3px 6px 0 rgba(0,0,0,0.2)", // 2
                    "0 4px 8px 0 rgba(0,0,0,0.2)", // 3
                    "0 6px 12px 0 rgba(0,0,0,0.2)", // 4
                    "0 8px 16px 0 rgba(0,0,0,0.2)", // 5
                    "0 12px 24px 0 rgba(0,0,0,0.2)", // 6
                    "0 16px 32px 0 rgba(0,0,0,0.2)", // 7
                    "0 24px 48px 0 rgba(0,0,0,0.2)", // 8
                ],
                breakpoints: {
                    values: {
                        xs: 0,
                        sm: 600,
                        md: 960,
                        lg: 1280,
                        xl: 1920
                    },
                },
            }),
        [mode]
    );

    return (
        <ColorModeContext.Provider value={{ toggleColorMode }}>
            <ThemeProvider theme={mode === "light" ? lightTheme : darkTheme}>{children}</ThemeProvider>
        </ColorModeContext.Provider>
    );
};

export default ThemeModeProvider;
